import type WhiteboardController from '../controllers/WhiteboardController';
import { type fabric } from 'fabric';
import Connector, {
  type Attachable,
  type Attachment,
} from '../objects/Connector';
import { AttachmentPoint, ConnectorEnd, type RecordReference } from '../types';
import ToolDelete from '../tools/ToolDelete';
import {
  hasRecordLinks,
  hasRecordReference,
  isDependencyConnector,
} from '../objects/guards';
import type Shape from '../objects/Shape';
import type ShapeRecord from '../shapes/ShapeRecord';
import {
  CONNECTOR_DEFAULT_WIDTH,
  CONNECTOR_ROUTING_ORTHOGONAL_AUTO,
} from './DefaultsManager';
import { CONNECTOR_END_STYLE, STROKE_TYPES } from '../constants';
import { type TConnectorRouting } from '../objects/ConnectorRouting';

type RecordObjectsMap = Map<string, Shape<ShapeRecord> & RecordReference>;

class DependencyLineManager {
  controller: WhiteboardController;

  constructor(controller: WhiteboardController) {
    this.controller = controller;
  }

  // Get all record objects on the canvas that have parent record links
  getRecordObjectsMap(): RecordObjectsMap {
    return this.controller.canvas
      .getObjects()
      .filter(hasRecordReference)
      .reduce((acc, obj) => {
        if (obj.recordId) {
          acc.set(obj.recordId, obj);
        }
        return acc;
      }, new Map());
  }

  // Get all dependency line connector objects on the canvas
  getDependencyLineObjects(): Connector[] {
    return this.controller.canvas.getObjects().filter(isDependencyConnector);
  }

  async updateRecordInfo(recordObjects: RecordObjectsMap): Promise<void> {
    return await Promise.all(
      Array.from(recordObjects).map(
        async ([, obj]) =>
          await this.controller.fetchRecordInfo(obj.recordType, obj.recordId)
      )
    ).then(res => {
      res.forEach(record => {
        if (record?.id) {
          const obj = recordObjects.get(record.id);
          if (obj) obj.shape.recordInfo = record;
        }
      });
    });
  }

  objectHasParentRecordLinks(obj: Shape<ShapeRecord>) {
    const { recordInfo } = obj.shape;

    if (!hasRecordLinks(recordInfo) || !recordInfo.parentRecordLinks) {
      return false;
    }

    return recordInfo.parentRecordLinks.length > 0;
  }

  dependencyColor(linkType: string) {
    if (['BLOCKS', 'DEPENDS_ON'].includes(linkType)) {
      return '#BA4111';
    }
    if (linkType === 'RELATES_TO') {
      return '#666666';
    }
    if (linkType === 'CONTAINS') {
      return '#0073CF';
    }
    return '#666666';
  }

  dependencyTextWidth(linkType: string) {
    // We're hard coding widths here which isn't ideal but
    // if we don't do this the labels will look funky.
    if (linkType === 'BLOCKS') return 51;
    if (linkType === 'DEPENDS_ON') return 86;
    if (linkType === 'IMPACTS') return 59;
    if (linkType === 'RELATES_TO') return 80;
    if (linkType === 'CONTAINS') return 70;
    return 80; // Default width for unknown types
  }

  /**
   * Calculate text width for a given relation description
   * This can be used by diagrams that need to create labels
   */
  static calculateLabelWidth(relationDescription: string): number {
    // Approximate width calculation: ~8 pixels per character + padding
    return Math.max(50, relationDescription.length * 8 + 20);
  }

  labelDefaultProperties() {
    return {
      type: 'shape',
      shapeName: 'text',
      strokeType: null,
      left: 0,
      top: 0,
      scaleX: 1,
      scaleY: 1,
      stroke: null,
      strokeWidth: 0,
      opacity: 1,
      fill: null,
      fontSize: 14,
      angle: 0,
      labelOrientation: 'inline',
      height: 30,
      labelPosition: 0.5,
      showAuthor: true,
    };
  }

  /**
   * Get connector properties for a given link type without creating the connector
   * This allows the properties to be used in diagram edge attributes
   */
  getConnectorProperties(linkType: string) {
    const color = this.dependencyColor(linkType);

    let endStyles = [
      CONNECTOR_END_STYLE.NONE,
      CONNECTOR_END_STYLE.FILLED_ARROW,
    ];

    if (linkType === 'RELATES_TO') {
      endStyles = [
        CONNECTOR_END_STYLE.FILLED_ARROW,
        CONNECTOR_END_STYLE.FILLED_ARROW,
      ];
    }

    return {
      stroke: color,
      strokeWidth: CONNECTOR_DEFAULT_WIDTH,
      endStyles,
    };
  }

  createConnector({
    parentObj,
    childObj,
    recordLink,
    lineRouting = CONNECTOR_ROUTING_ORTHOGONAL_AUTO,
    attachments = [],
  }: {
    parentObj: Attachable;
    childObj: Attachable;
    recordLink: Aha.RecordLink;
    lineRouting?: TConnectorRouting;
    attachments?: [Attachment, Attachment] | [];
  }): fabric.Object {
    const {
      stroke: color,
      strokeWidth,
      endStyles,
    } = this.getConnectorProperties(recordLink.linkType);
    const width = this.dependencyTextWidth(recordLink.linkType);

    const connector = Connector.create(
      [
        { x: 0, y: 0 },
        { x: 1, y: 1 },
      ],
      {
        controller: this.controller,
        ...this.controller.defaultsManager.for('connector'),
        drawing: false,
        strokeWidth,
        lineRouting,
        strokeType: STROKE_TYPES.SOLID,
        stroke: color,
        dependencyLineInfo: {
          parentRecordId: parentObj.recordId,
          childRecordId: childObj.recordId,
          linkType: recordLink.linkType,
        },
        endStyles,
      }
    );

    // We want to reverse the arrows so it's 'Dependency of'
    const invertRecordLink = recordLink.linkType === 'DEPENDS_ON';
    let relationText = recordLink.relationDescription;

    // Attach the connector
    if (invertRecordLink) {
      relationText = 'Dependency of';

      const parentAttachmentPoint =
        attachments[0]?.attachmentPoint || AttachmentPoint.Left;
      const childAttachmentPoint =
        attachments[1]?.attachmentPoint || AttachmentPoint.Right;
      parentObj.attachConnector(
        connector,
        ConnectorEnd.CONNECTOR_END,
        parentAttachmentPoint
      );
      childObj.attachConnector(
        connector,
        ConnectorEnd.CONNECTOR_START,
        childAttachmentPoint
      );
    } else {
      const parentAttachmentPoint =
        attachments[0]?.attachmentPoint || AttachmentPoint.Right;
      const childAttachmentPoint =
        attachments[1]?.attachmentPoint || AttachmentPoint.Left;
      parentObj.attachConnector(
        connector,
        ConnectorEnd.CONNECTOR_START,
        parentAttachmentPoint
      );
      childObj.attachConnector(
        connector,
        ConnectorEnd.CONNECTOR_END,
        childAttachmentPoint
      );
    }
    connector.constrainConnector({});

    connector.createLabel({
      labelText: `<p><em><span style="color:${color}">${relationText}</span></em></p>`,
      width,
    });
    const label = connector.labels[0];

    connector.toggleLabelAlignment();
    label.updateLabelPositionAndAngle(connector);
    this.controller.canvas.discardActiveObject();
    return connector;
  }

  deleteDependencyLines() {
    const dependencyLineObjects = this.getDependencyLineObjects();
    ToolDelete.deleteObjects(dependencyLineObjects, this.controller);
  }

  refreshDependencyLines() {
    const recordObjects = this.getRecordObjectsMap();

    this.updateRecordInfo(recordObjects)
      .then(() => {
        const dependencyLineObjects = this.getDependencyLineObjects();

        recordObjects.forEach(parentObj => {
          const { recordInfo } = parentObj.shape;

          if (!hasRecordLinks(recordInfo)) return;

          const parentRecordLinks = recordInfo.parentRecordLinks || [];

          if (parentRecordLinks.length > 0) {
            parentRecordLinks.forEach(recordLink => {
              const childObj = recordObjects.get(recordLink.childRecordId);

              if (!childObj?.shape?.recordInfo) return;

              const connectorIndex = dependencyLineObjects.findIndex(
                ({ dependencyLineInfo }) =>
                  dependencyLineInfo?.parentRecordId === parentObj.recordId &&
                  dependencyLineInfo?.childRecordId === childObj?.recordId &&
                  dependencyLineInfo?.linkType === recordLink.linkType
              );
              const connector = dependencyLineObjects[connectorIndex];

              if (childObj && !connector) {
                // Connector has been removed but records still exist
                this.createConnector({
                  parentObj,
                  childObj,
                  recordLink,
                });
              } else if (childObj && connector) {
                // Records and connector still exist
                // Is connector still attached to the correct records?
                this.maybeReattachConnectors(connector, parentObj, childObj);

                // Remove from array so we don't delete it later
                dependencyLineObjects.splice(connectorIndex, 1);
              }
            });
          }
        });

        if (dependencyLineObjects.length) {
          // Delete any orphaned connectors
          ToolDelete.deleteObjects(dependencyLineObjects, this.controller);
        }

        this.controller.canvas.requestRenderAll();
        this.controller.undoManager.completeUndoGroup();
      })
      .catch(e => {
        console.error(e);
      });
  }

  maybeReattachConnectors(
    connector: Connector,
    parentObj: Shape<ShapeRecord>,
    childObj: Shape<ShapeRecord>
  ) {
    const startObj =
      connector.attachments[ConnectorEnd.CONNECTOR_START]?.object;
    const endObj = connector.attachments[ConnectorEnd.CONNECTOR_END]?.object;
    const reattachParent = startObj !== parentObj;
    const reattachChild = endObj !== childObj;

    // Both ends have to be disconnected BEFORE either can be reconnected
    if (reattachParent && startObj) {
      connector.detachObject(startObj);
    }
    if (reattachChild && endObj) {
      connector.detachObject(endObj);
    }

    if (reattachParent) {
      parentObj.attachConnector(
        connector,
        ConnectorEnd.CONNECTOR_START,
        AttachmentPoint.Right
      );
    }
    if (reattachChild) {
      childObj.attachConnector(
        connector,
        ConnectorEnd.CONNECTOR_END,
        AttachmentPoint.Left
      );
    }
    connector.constrainConnector({});
  }
}

export default DependencyLineManager;
